/**
 * Sales Dashboard - Main dashboard for sales users
 *
 * Shows follow-up reminders, commission summary, pipeline overview,
 * and territory opportunity metrics.
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Phone,
  AlertTriangle,
  PoundSterling,
  TrendingUp,
  MapPin,
  Users,
  RefreshCw,
  ExternalLink,
  Target,
  CheckCircle,
} from 'lucide-react'
import { getSalesDashboard } from '@/services/salesApi'
import type { SalesDashboard, FollowUpReminder, PipelineStageCount } from '@/types'

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  new: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'New' },
  contacted: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Contacted' },
  warm: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Warm' },
  in_discussion: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'In Discussion' },
  won: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Won' },
  not_interested: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Not Interested' },
  lost: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Lost' },
}

export default function SalesDashboard() {
  const [data, setData] = useState<SalesDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const dashboard = await getSalesDashboard()
      setData(dashboard)
    } catch {
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-slate-300 mb-2">{error}</p>
        <button
          onClick={fetchDashboard}
          className="btn-secondary text-sm"
        >
          Try Again
        </button>
      </div>
    )
  }

  const followUps = data?.followUps
  const commission = data?.commission
  const pipeline = data?.pipeline
  const opportunity = data?.opportunity

  // Combine and sort follow-ups
  const allFollowUps = [
    ...(followUps?.overdueProspects || []),
    ...(followUps?.dueTodayProspects || []),
  ].sort((a, b) => a.daysOverdue - b.daysOverdue).reverse()

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Sales Dashboard</h1>
          <p className="text-slate-400 mt-1">Your sales performance at a glance</p>
        </div>
        <button
          onClick={fetchDashboard}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Follow-up Alerts */}
      {(followUps?.overdue || 0) > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-red-300 font-medium">
                {followUps?.overdue} overdue follow-up{followUps?.overdue !== 1 ? 's' : ''}
              </p>
              <p className="text-red-400/70 text-sm">
                Don't let these prospects go cold!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid - Commission Summary */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="card">
          <div className="flex items-start justify-between">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
              <PoundSterling className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-slate-100">
              {formatCurrency(commission?.totalEarnedThisMonth || 0)}
            </p>
            <p className="text-sm text-slate-400 mt-1">Earned This Month</p>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-slate-100">
              {formatCurrency(commission?.planningTotal || 0)}
            </p>
            <p className="text-sm text-slate-400 mt-1">Monthly Recurring</p>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-emerald-400">
                {commission?.planningAtTenPercentClients || 0} @ 10%
              </span>
              <span className="text-amber-400">
                {commission?.planningAtFivePercentClients || 0} @ 5%
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 shadow-lg">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-slate-100">
              {pipeline?.totalActive || 0}
            </p>
            <p className="text-sm text-slate-400 mt-1">Active Prospects</p>
            <p className="text-xs text-emerald-400 mt-2">
              {pipeline?.conversionRate?.toFixed(1) || 0}% conversion rate
            </p>
          </div>
        </div>

        <div className="card">
          <div className="flex items-start justify-between">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
              <MapPin className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-slate-100">
              {(opportunity?.builderSlotsAvailable || 0) + (opportunity?.treeSurgeonSlotsAvailable || 0)}
            </p>
            <p className="text-sm text-slate-400 mt-1">Open Slots</p>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-blue-400">
                {opportunity?.builderSlotsAvailable || 0} Builder
              </span>
              <span className="text-green-400">
                {opportunity?.treeSurgeonSlotsAvailable || 0} Tree
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Follow-ups */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-100">Follow-ups</h2>
                <p className="text-xs text-slate-500">
                  {followUps?.dueToday || 0} today, {followUps?.overdue || 0} overdue
                </p>
              </div>
            </div>
            <Link
              to="/sales/planning"
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
            >
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          {allFollowUps.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-500/50 mx-auto mb-3" />
              <p className="text-slate-400">All caught up!</p>
              <p className="text-slate-500 text-sm mt-1">No follow-ups due</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
              {allFollowUps.slice(0, 8).map((fu) => (
                <FollowUpCard key={fu.prospectId} followUp={fu} />
              ))}
            </div>
          )}
        </div>

        {/* Pipeline Stages */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-100">Pipeline</h2>
                <p className="text-xs text-slate-500">
                  {pipeline?.totalActive || 0} active prospects
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {pipeline?.stages
              ?.filter((s) => ['new', 'contacted', 'warm', 'in_discussion'].includes(s.status))
              .map((stage) => (
                <PipelineStageBar key={stage.status} stage={stage} />
              ))}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-700/50">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Outcomes</h3>
            <div className="grid grid-cols-3 gap-3">
              {pipeline?.stages
                ?.filter((s) => ['won', 'not_interested', 'lost'].includes(s.status))
                .map((stage) => {
                  const config = STATUS_COLORS[stage.status] || STATUS_COLORS.new
                  return (
                    <div key={stage.status} className={`p-3 rounded-lg ${config.bg}`}>
                      <p className={`text-2xl font-bold ${config.text}`}>{stage.count}</p>
                      <p className="text-xs text-slate-400">{config.label}</p>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>

      {/* Commission Breakdown */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
              <PoundSterling className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Commission Breakdown</h2>
              <p className="text-xs text-slate-500">Your current earnings structure</p>
            </div>
          </div>
          <Link
            to="/sales/commission"
            className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
          >
            View details <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded">
                10% Rate
              </span>
              <span className="text-xs text-slate-500">
                {commission?.planningAtTenPercentClients || 0} clients
              </span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {formatCurrency(commission?.planningAtTenPercent || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Year 1-2 clients</p>
          </div>

          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded">
                5% Rate
              </span>
              <span className="text-xs text-slate-500">
                {commission?.planningAtFivePercentClients || 0} clients
              </span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {formatCurrency(commission?.planningAtFivePercent || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Year 3+ clients</p>
          </div>

          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
                Websites
              </span>
              <span className="text-xs text-slate-500">
                {commission?.websiteThisMonthCount || 0} this month
              </span>
            </div>
            <p className="text-2xl font-bold text-purple-400">
              {formatCurrency(commission?.websiteThisMonth || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-1">One-time ({formatCurrency(120)} each)</p>
          </div>

          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
                Pending
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-400">
              {formatCurrency(commission?.pendingPayment || 0)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Awaiting client payment</p>
          </div>
        </div>

        {/* Full Potential Section */}
        <div className="mt-6 pt-4 border-t border-slate-700/50">
          <h3 className="text-sm font-medium text-slate-300 mb-4">Full Capacity Potential</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="p-3 rounded-lg bg-slate-800/50">
              <p className="text-xs text-slate-500 mb-1">Total Territories</p>
              <p className="text-xl font-bold text-slate-200">{opportunity?.totalTerritories || 396}</p>
              <p className="text-xs text-slate-500 mt-1">× 2 slots each = {(opportunity?.totalTerritories || 396) * 2} clients</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs text-emerald-400 mb-1">Max at 10% (Years 1-2)</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(35640)}</p>
              <p className="text-xs text-slate-500 mt-1">{(opportunity?.totalTerritories || 396) * 2} × £22.50/month</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400 mb-1">Max at 5% (Year 3+)</p>
              <p className="text-xl font-bold text-amber-400">{formatCurrency(17820)}</p>
              <p className="text-xs text-slate-500 mt-1">{(opportunity?.totalTerritories || 396) * 2} × £11.25/month</p>
            </div>
          </div>

          {/* Progress bar */}
          {opportunity && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Your progress</span>
                <span className="text-sm text-slate-300">
                  {formatCurrency(opportunity.currentMonthly || 0)} / {formatCurrency(35640)}
                </span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(((opportunity.currentMonthly || 0) / 35640) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>
                  {opportunity.builderSlotsAvailable || 0} builder + {opportunity.treeSurgeonSlotsAvailable || 0} tree surgeon slots available
                </span>
                <span>{(((opportunity.currentMonthly || 0) / 35640) * 100).toFixed(1)}% of max potential</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function FollowUpCard({ followUp }: { followUp: FollowUpReminder }) {
  const isOverdue = followUp.daysOverdue > 0

  return (
    <Link
      to={`/sales/planning?prospect=${followUp.prospectId}`}
      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isOverdue ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
          <Phone className={`w-4 h-4 ${isOverdue ? 'text-red-400' : 'text-amber-400'}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-200">{followUp.companyName}</p>
          <p className="text-xs text-slate-500">{followUp.contactName}</p>
        </div>
      </div>
      <div className="text-right">
        {isOverdue ? (
          <span className="text-xs font-medium text-red-400">
            {followUp.daysOverdue}d overdue
          </span>
        ) : (
          <span className="text-xs font-medium text-amber-400">Due today</span>
        )}
        <p className="text-xs text-slate-500">{followUp.contactPhone}</p>
      </div>
    </Link>
  )
}

function PipelineStageBar({ stage }: { stage: PipelineStageCount }) {
  const config = STATUS_COLORS[stage.status] || STATUS_COLORS.new
  const percentage = stage.percentage || 0

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-300">{config.label}</span>
        <span className="text-sm text-slate-400">{stage.count}</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${config.text.replace('text-', 'bg-')}`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
    </div>
  )
}
